<script>
/**
 * BaileysConnectionModal.vue
 * Componente simplificado para exibir QR Code e status de conexao Baileys
 * Usado dentro do modal no ContactPanel
 */
import { mapGetters } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'BaileysConnectionModal',
  components: {
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  emits: [],
  data() {
    return {
      qrCodeData: null,
      isLoadingQR: false,
      isCheckingStatus: false,
      isConnecting: false,
      isDisconnecting: false,
      errorMessage: null,
    };
  },
  computed: {
    ...mapGetters({
      getBaileysConnectionStatus: 'inboxes/getBaileysConnectionStatus',
    }),
    connectionStatus() {
      return this.getBaileysConnectionStatus(this.inbox.id);
    },
    statusConfig() {
      const rawStatus = this.connectionStatus?.status || '';
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      const configs = {
        OPEN: {
          label: 'Conectado',
          color: 'bg-n-teal-9',
          textColor: 'text-n-teal-11',
          bgColor: 'bg-n-teal-3',
          borderColor: 'border-n-teal-6',
        },
        CONNECTED: {
          label: 'Conectado',
          color: 'bg-n-teal-9',
          textColor: 'text-n-teal-11',
          bgColor: 'bg-n-teal-3',
          borderColor: 'border-n-teal-6',
        },
        CONNECTING: {
          label: 'Conectando...',
          color: 'bg-n-amber-9',
          textColor: 'text-n-amber-11',
          bgColor: 'bg-n-amber-3',
          borderColor: 'border-n-amber-6',
        },
        CLOSE: {
          label: 'Desconectado',
          color: 'bg-n-ruby-9',
          textColor: 'text-n-ruby-11',
          bgColor: 'bg-n-ruby-3',
          borderColor: 'border-n-ruby-6',
        },
        DISCONNECTED: {
          label: 'Desconectado',
          color: 'bg-n-ruby-9',
          textColor: 'text-n-ruby-11',
          bgColor: 'bg-n-ruby-3',
          borderColor: 'border-n-ruby-6',
        },
        NOT_FOUND: {
          label: 'Instancia nao encontrada',
          color: 'bg-n-amber-9',
          textColor: 'text-n-amber-11',
          bgColor: 'bg-n-amber-3',
          borderColor: 'border-n-amber-6',
        },
        ERROR: {
          label: 'Erro na conexao',
          color: 'bg-n-ruby-9',
          textColor: 'text-n-ruby-11',
          bgColor: 'bg-n-ruby-3',
          borderColor: 'border-n-ruby-6',
        },
      };

      return configs[status] || {
        label: 'Verificando...',
        color: 'bg-n-slate-9',
        textColor: 'text-n-slate-11',
        bgColor: 'bg-n-slate-3',
        borderColor: 'border-n-slate-6',
      };
    },
    isConnected() {
      const status = (this.connectionStatus?.status || '').toUpperCase();
      return status === 'OPEN' || status === 'CONNECTED';
    },
    phoneNumber() {
      return this.connectionStatus?.phone_number || this.connectionStatus?.phoneNumber || this.inbox.phone_number;
    },
  },
  mounted() {
    this.checkStatus();

    emitter.on(BUS_EVENTS.BAILEYS_CONNECTED, this.handleBaileysConnected);
    emitter.on(BUS_EVENTS.BAILEYS_QR_GENERATED, this.handleBaileysQRGenerated);
  },
  beforeUnmount() {
    emitter.off(BUS_EVENTS.BAILEYS_CONNECTED, this.handleBaileysConnected);
    emitter.off(BUS_EVENTS.BAILEYS_QR_GENERATED, this.handleBaileysQRGenerated);
  },
  methods: {
    handleBaileysConnected(data) {
      // inbox_id já vem como Number do actionCable
      if (data.inbox_id !== this.inbox.id) return;

      this.qrCodeData = null;
      useAlert('WhatsApp conectado com sucesso!');
      // Não emite mais 'connected' - a store cuida da reatividade
    },

    handleBaileysQRGenerated(data) {
      if (Number(data.inbox_id) !== this.inbox.id) return;
      this.fetchQRCode();
    },

    async fetchQRCode() {
      if (!this.inbox.id) return;

      this.isLoadingQR = true;
      this.errorMessage = null;

      try {
        const response = await InboxesAPI.baileysGetQRCode(this.inbox.id);

        if (response.data?.success) {
          this.connectionStatus = response.data.status;

          if (this.isConnected) {
            useAlert('WhatsApp ja esta conectado!');
            this.qrCodeData = null;
            return;
          }

          const qrBase64 = response.data.qrcode?.base64;

          if (qrBase64 && qrBase64.trim() !== '') {
            this.qrCodeData = qrBase64;
          } else {
            this.errorMessage = 'QR Code nao disponivel';
            this.qrCodeData = null;
          }
        } else {
          this.errorMessage = response.data?.error || 'Erro ao obter QR Code';
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.error || 'Erro ao obter QR Code';
      } finally {
        this.isLoadingQR = false;
      }
    },

    async checkStatus() {
      if (!this.inbox.id) return;

      this.isCheckingStatus = true;
      this.errorMessage = null;

      try {
        const response = await InboxesAPI.baileysGetStatus(this.inbox.id);

        if (response.data?.success) {
          this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
            inboxId: this.inbox.id,
            status: response.data.data,
          });
        } else {
          this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
            inboxId: this.inbox.id,
            status: { status: 'error' },
          });
        }
      } catch (error) {
        const status = error.response?.status === 404
          ? { status: 'not_found' }
          : { status: 'error' };
        this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
          inboxId: this.inbox.id,
          status,
        });
      } finally {
        this.isCheckingStatus = false;
      }
    },

    async connectInstance() {
      if (!this.inbox.id) return;

      this.isConnecting = true;
      this.errorMessage = null;

      try {
        await this.checkStatus();

        if (this.isConnected) {
          useAlert('WhatsApp ja esta conectado!');
          this.isConnecting = false;
          return;
        }

        try {
          await InboxesAPI.baileysCreateInstance(this.inbox.id);
        } catch (e) {
          // Ignorar erro se instância já existe
        }

        await InboxesAPI.baileysConnect(this.inbox.id);
        await this.fetchQRCode();

      } catch (error) {
        this.errorMessage = error.response?.data?.error || 'Erro ao conectar';
        useAlert(this.errorMessage);
      } finally {
        this.isConnecting = false;
      }
    },

    async disconnectInstance() {
      if (!this.inbox.id) return;

      this.isDisconnecting = true;
      this.errorMessage = null;

      try {
        await InboxesAPI.baileysDisconnect(this.inbox.id);

        // Atualizar store diretamente
        this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
          inboxId: this.inbox.id,
          status: { status: 'DISCONNECTED' },
        });
        this.qrCodeData = null;

        useAlert('WhatsApp desconectado com sucesso');
      } catch (error) {
        this.errorMessage = error.response?.data?.error || 'Erro ao desconectar';
        useAlert(this.errorMessage);
      } finally {
        this.isDisconnecting = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Status Card -->
    <div
      class="p-3 rounded-lg border flex items-center justify-between"
      :class="[statusConfig.bgColor, statusConfig.borderColor]"
    >
      <div class="flex items-center gap-3">
        <span
          class="w-3 h-3 rounded-full"
          :class="[statusConfig.color, { 'animate-pulse': !isConnected }]"
        />
        <div>
          <p class="font-medium text-sm" :class="statusConfig.textColor">
            {{ statusConfig.label }}
          </p>
          <p v-if="phoneNumber" class="text-xs text-n-slate-11">
            {{ phoneNumber }}
          </p>
        </div>
      </div>
      <NextButton
        :is-loading="isCheckingStatus"
        size="xs"
        ghost
        label="Atualizar"
        @click="checkStatus"
      />
    </div>

    <!-- Error Message -->
    <div
      v-if="errorMessage"
      class="p-3 rounded-lg border bg-n-ruby-3 border-n-ruby-6"
    >
      <p class="text-sm text-n-ruby-11">{{ errorMessage }}</p>
    </div>

    <!-- QR Code Section (when not connected) -->
    <div v-if="!isConnected" class="flex flex-col items-center">
      <!-- QR Code Display -->
      <div
        class="p-4 mb-4 rounded-lg bg-white dark:bg-n-slate-1"
        :class="{ 'animate-pulse': isLoadingQR }"
      >
        <img
          v-if="qrCodeData"
          :src="qrCodeData"
          alt="QR Code WhatsApp"
          class="w-56 h-56"
        />
        <div
          v-else
          class="flex items-center justify-center w-56 h-56 rounded bg-n-slate-3"
        >
          <span v-if="isLoadingQR" class="text-n-slate-11 text-sm">
            Carregando QR Code...
          </span>
          <span v-else class="text-n-slate-11 text-center text-sm px-4">
            Clique em 'Conectar' para gerar o QR Code
          </span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3 mb-4">
        <NextButton
          v-if="!qrCodeData"
          :is-loading="isConnecting"
          solid
          blue
          label="Conectar"
          @click="connectInstance"
        />
        <template v-else>
          <NextButton
            :is-loading="isLoadingQR"
            solid
            label="Atualizar QR"
            @click="fetchQRCode"
          />
          <NextButton
            :is-loading="isCheckingStatus"
            solid
            blue
            label="Verificar Conexao"
            @click="checkStatus"
          />
        </template>
      </div>

      <!-- Instructions -->
      <div class="w-full p-4 rounded-lg bg-n-slate-3">
        <h4 class="mb-2 font-medium text-sm text-n-slate-12">
          Como conectar:
        </h4>
        <ol class="list-decimal list-inside text-xs text-n-slate-11 space-y-1">
          <li>Abra o WhatsApp no seu celular</li>
          <li>Toque em Menu ou Configuracoes e selecione 'Aparelhos conectados'</li>
          <li>Toque em 'Conectar um aparelho'</li>
          <li>Aponte seu celular para esta tela para escanear o QR Code</li>
        </ol>
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="flex flex-col items-center py-4">
      <div class="w-16 h-16 rounded-full bg-n-teal-3 flex items-center justify-center mb-3">
        <span class="i-lucide-check text-3xl text-n-teal-11" />
      </div>
      <h3 class="text-base font-medium text-n-slate-12 mb-1">
        WhatsApp Conectado
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        Sua instancia esta pronta para enviar e receber mensagens.
      </p>
      <!-- Disconnect Button -->
      <NextButton
        :is-loading="isDisconnecting"
        ruby
        label="Desconectar"
        icon="i-lucide-log-out"
        @click="disconnectInstance"
      />
    </div>
  </div>
</template>
